import JSZip from "jszip";
import { Finding, Severity } from "../types";

const severityColors: Record<Severity, string> = {
  [Severity.NONE]: '#22c55e',
  [Severity.LOW]: '#0ea5e9',
  [Severity.MEDIUM]: '#eab308',
  [Severity.HIGH]: '#f97316',
  [Severity.CRITICAL]: '#ef4444',
};

const drawAnnotationOnCanvas = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  finding: Finding,
  width: number,
  height: number
) => {
  // Draw base image
  ctx.drawImage(image, 0, 0, width, height);

  // Draw Box
  if (finding.box) {
    const [ymin, xmin, ymax, xmax] = finding.box;
    const color = severityColors[finding.severity] || '#f97316';

    const x = (xmin / 1000) * width;
    const y = (ymin / 1000) * height;
    const w = ((xmax - xmin) / 1000) * width;
    const h = ((ymax - ymin) / 1000) * height;

    ctx.strokeStyle = color;
    ctx.lineWidth = 5;
    ctx.strokeRect(x, y, w, h);

    // Draw Label Background
    ctx.fillStyle = color;
    ctx.font = 'bold 24px monospace';
    const text = `${finding.type} [${finding.severity}]`;
    const textMetrics = ctx.measureText(text);
    const textBgHeight = 34;

    ctx.fillRect(x, y - textBgHeight, textMetrics.width + 20, textBgHeight);

    // Draw Label Text
    ctx.fillStyle = 'white';
    ctx.fillText(text, x + 10, y - 8);
  }

  // Draw Footer info
  if (finding.spatialCoords) {
    const footerH = 60;
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(0, height - footerH, width, footerH);

    ctx.fillStyle = '#fff';
    ctx.font = '20px monospace';
    const h = finding.spatialCoords.z.toFixed(2);
    const az = finding.azimuth ? Math.round(finding.azimuth) : 0;
    ctx.fillText(`LOC: H=${h}m | AZIMUTH: ${az}° | TYPE: ${finding.type.toUpperCase()}`, 20, height - 20);
  }
};

export const exportFindingsToZip = async (
  findings: Finding[],
  projectName: string = "SST_Audit"
): Promise<void> => {
  const zip = new JSZip();
  const folder = zip.folder(`${projectName}_Evidence_V6.0`);

  if (!folder) throw new Error("Could not create ZIP folder");

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) throw new Error("Canvas context initialization failed");

  // Process serially to manage memory
  for (let idx = 0; idx < findings.length; idx++) {
    const finding = findings[idx];
    if (finding.frameImage && finding.frameImage.startsWith("data:image")) {

      await new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;

          // Burn annotation onto the image
          drawAnnotationOnCanvas(ctx, img, finding, img.width, img.height);

          const burnedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
          const base64Data = burnedDataUrl.split(",")[1];

          const hStr = finding.spatialCoords ? `_H${finding.spatialCoords.z.toFixed(1)}m` : '';
          const azStr = finding.azimuth ? `_AZ${Math.round(finding.azimuth)}` : '';

          const fileName = `EV_V6.0_${(idx + 1).toString().padStart(3, '0')}_${finding.severity}_${finding.type.replace(/\s+/g, '_')}${hStr}${azStr}.jpg`;

          folder.file(fileName, base64Data, { base64: true });
          resolve();
        };
        img.onerror = () => resolve(); // Skip on error
        img.src = finding.frameImage!;
      });
    }
  }

  const content = await zip.generateAsync({ type: "blob" });
  const filename = `${projectName}_Evidence_Bundle_${Math.floor(Date.now() / 1000)}.zip`;

  if ('showSaveFilePicker' in window) {
    try {
      const handle = await (window as any).showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'ZIP Archive', accept: { 'application/zip': ['.zip'] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(content);
      await writable.close();
      return;
    } catch (err: any) {
      if (err.name === 'AbortError') return;
    }
  }

  // Fallback for browsers without File System Access API
  const reader = new FileReader();
  reader.onload = () => {
    const url = reader.result as string;
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => document.body.removeChild(link), 1000);
  };
  reader.readAsDataURL(content);
};